/**
 * v3.21-1667 · Note attention_color rendering utility.
 *
 * Notes carry an operator-chosen attention_color:
 *   neutral | green | yellow | red
 *
 * Rendering rules:
 *   • Neutral / missing → existing card appearance (never modified).
 *   • Green   → emerald left accent, subtle emerald tint, semantic label.
 *   • Yellow  → amber left accent, subtle amber tint, semantic label.
 *   • Red     → red left accent, subtle red tint, semantic label.
 *
 * Isolation guarantee (backend enforced): color NEVER alters
 * Deal lifecycle, paid_in_full, all_parties_settled, or any
 * financial field.  It is UI attention only.
 */

export const ATTENTION_COLORS = ['neutral', 'green', 'yellow', 'red'];

const PALETTE = {
  green: {
    accent: 'border-l-4 border-l-emerald-500',
    tint:   'bg-emerald-50/70',
    label:  'Attention · Green',
    badge:  'bg-emerald-100 text-emerald-800 border-emerald-300',
    dot:    'bg-emerald-500',
  },
  yellow: {
    accent: 'border-l-4 border-l-amber-500',
    tint:   'bg-amber-50/70',
    label:  'Attention · Yellow',
    badge:  'bg-amber-100 text-amber-800 border-amber-300',
    dot:    'bg-amber-500',
  },
  red: {
    accent: 'border-l-4 border-l-red-500',
    tint:   'bg-red-50/70',
    label:  'Attention · Red',
    badge:  'bg-red-100 text-red-800 border-red-300',
    dot:    'bg-red-500',
  },
};

/**
 * Normalise a note document's attention_color to a canonical value.
 * Undefined / null / empty / unknown → 'neutral'.
 */
export function normalizeAttentionColor(raw) {
  if (!raw) return 'neutral';
  const v = String(raw).trim().toLowerCase();
  return ATTENTION_COLORS.includes(v) ? v : 'neutral';
}

/**
 * Return classNames + label metadata for a given attention_color.
 * Returns `null` for neutral so callers can short-circuit and keep
 * the existing card appearance.
 */
export function getAttentionStyle(color) {
  const c = normalizeAttentionColor(color);
  if (c === 'neutral') return null;
  return { color: c, ...PALETTE[c] };
}
